export const state = () => ({
  selectedRestaurants: [],
  selectedTimeRange: "",
  selectedRangeInterval: "",
  selectedStartDate: "",
  selectedEndDate: "",
  selectedOrderFilter: {
    selectedStages: [],
    selectedChannels: [],
    selectedPayments: []
  },
  ordersSearch: "",
  orderFilterCount: 0,
  products: [],
  summary: [],
  aggregates: {},
  categories: [],
  subCategories: [],
  reportLoading: false,
  reportOrders: [],
  ordersLoading: false,
  reportInsights: {},
  reportInsightsLoading: false,
  reportCanellationReasons: {},
  reportCanellationReasonsLoading: false,
  businessGraphData: {},
  businessGraphDataLoading: false,
  peakHours: [],
  peakHoursLoading: false,
  orderStages: {},
  orderStagesLoading: false,
  orderDuration: {},
  orderDurationLoading: false,
  deliveredLocations: {},
  deliveredLocationsLoading: false,
  storeAvailability: {},
  storeAvailabilityLoading: false
});

export const mutations = {
  SET_REPORT_LOADING(state, reportLoading) {
    state.reportLoading = reportLoading;
  },
  SET_ORDER_LOADING(state, ordersLoading) {
    state.ordersLoading = ordersLoading;
  },
  SET_DATE_TIME_RANGE(
    state,
    {
      selectedStartDate,
      selectedEndDate,
      selectedTimeRange,
      selectedRangeInterval
    }
  ) {
    state.selectedTimeRange = selectedTimeRange || state.selectedTimeRange;
    state.selectedRangeInterval =
      selectedRangeInterval || state.selectedRangeInterval;
    state.selectedStartDate = selectedStartDate || state.selectedStartDate;
    state.selectedEndDate = selectedEndDate || state.selectedEndDate;
  },
  SET_REPORT_RESTAURANT(state, selectedRestaurants) {
    state.selectedRestaurants = selectedRestaurants;
  },
  SET_REPORT_PRODUCTS(state, products) {
    state.products = products;
  },
  SET_REPORT_SUMMARY(state, summary) {
    state.summary = summary;
  },
  SET_REPORT_AGGREGATES(state, aggregates) {
    state.aggregates = aggregates;
  },
  SET_REPORT_CATEGORIES(state, categories) {
    state.categories = categories;
  },
  SET_REPORT_SUB_CATEGORIES(state, subCategories) {
    state.subCategories = subCategories;
  },
  SET_ORDERS(state, reportOrders) {
    state.reportOrders = reportOrders;
  },
  SET_ORDER_INSIGHTS(state, reportInsights) {
    state.reportInsights = reportInsights;
  },
  SET_REPORT_INSIGHT_LOADING(state, reportInsightsLoading) {
    state.reportInsightsLoading = reportInsightsLoading;
  },
  SET_REPORT_CANCELLATION_REASONS(state, reportCanellationReasons) {
    state.reportCanellationReasons = reportCanellationReasons;
  },
  SET_REPORT_CANCELLATION_REASONS_LOADING(
    state,
    reportCanellationReasonsLoading
  ) {
    state.reportCanellationReasonsLoading = reportCanellationReasonsLoading;
  },
  SET_BUSINESS_GRAPH_DATA(state, businessGraphData) {
    state.businessGraphData = businessGraphData;
  },
  SET_BUSINESS_GRAPH_DATA_LOADING(state, businessGraphDataLoading) {
    state.businessGraphDataLoading = businessGraphDataLoading;
  },
  SET_PEAK_HOURS(state, peakHours) {
    state.peakHours = peakHours;
  },
  SET_PEAK_HOURS_LOADING(state, peakHoursLoading) {
    state.peakHoursLoading = peakHoursLoading;
  },
  SET_ORDER_STAGES(state, orderStages) {
    state.orderStages = orderStages;
  },
  SET_ORDER_STAGES_LOADING(state, orderStagesLoading) {
    state.orderStagesLoading = orderStagesLoading;
  },
  SET_ORDER_DURATION(state, orderDuration) {
    state.orderDuration = orderDuration;
  },
  SET_ORDER_DURATION_LOADING(state, orderDurationLoading) {
    state.orderDurationLoading = orderDurationLoading;
  },
  SET_DELIVERED_LOCATIONS(state, deliveredLocations) {
    state.deliveredLocations = deliveredLocations;
  },
  SET_DELIVERED_LOCATIONS_LOADING(state, deliveredLocationsLoading) {
    state.deliveredLocationsLoading = deliveredLocationsLoading;
  },
  SET_STORE_AVAILABILITY(state, storeAvailability) {
    state.storeAvailability = storeAvailability;
  },
  SET_STORE_AVAILABILITY_LOADING(state, storeAvailabilityLoading) {
    state.storeAvailabilityLoading = storeAvailabilityLoading;
  },
  SET_ORDER_CHANNEL_FILTER(state, selectedChannels) {
    state.selectedOrderFilter.selectedChannels = selectedChannels;
  },
  SET_ORDER_STAGE_FILTER(state, selectedStages) {
    state.selectedOrderFilter.selectedStages = selectedStages;
  },
  SET_ORDER_PAYMENT_FILTER(state, selectedPayments) {
    state.selectedOrderFilter.selectedPayments = selectedPayments;
  },
  SET_ORDER_FILTER_COUNT(state, count) {
    state.orderFilterCount = count;
  },
  SET_ORDERS_SEARCH(state, ordersSearch) {
    state.ordersSearch = ordersSearch;
  }
};

export const actions = {
  getOverallSalesReport(
    { getters, commit, state },
    { data, backgroundLoad, force }
  ) {
    if (!force && state.products?.length) return;
    if (!backgroundLoad || !state.products?.length)
      commit("SET_REPORT_LOADING", true);
    return new Promise((resolve, reject) => {
      this.$axios
        .get(`/reports/overall_sales`, {
          params: getters.getDateRangePayload(data),
          ...$nuxt.$configData?.apiV2Config
        })
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_REPORT_PRODUCTS", response.data?.products);
            commit("SET_REPORT_SUMMARY", response.data?.summary || []);
            commit(
              "SET_REPORT_AGGREGATES",
              response.data?.["aggregates.count_by_status"]
            );
            commit("SET_REPORT_CATEGORIES", response.data?.categories);
            commit("SET_REPORT_SUB_CATEGORIES", response.data?.subcategories);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_REPORT_LOADING", false);
        });
    });
  },
  getOrderReport({ commit, getters, state }, { data, backgroundLoad, force }) {
    if (!backgroundLoad || !state.reportOrders?.length)
      commit("SET_ORDER_LOADING", true);
    return new Promise((resolve, reject) => {
      this.$axios
        .get(`/reports/orders`, {
          params: getters.getDateRangePayload(data),
          ...$nuxt.$configData?.apiV2Config
        })
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_ORDERS", response.data?.orders);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_ORDER_LOADING", false);
        });
    });
  },
  getReportInsights(
    { commit, getters, state },
    { data, backgroundLoad, force }
  ) {
    if (!backgroundLoad || !state.reportOrders?.length)
      commit("SET_REPORT_INSIGHT_LOADING", true);
    return new Promise((resolve, reject) => {
      this.$axios
        .get(`/reports/insights`, {
          params: getters.getDateRangePayload(data),
          ...$nuxt.$configData?.apiV2Config
        })
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_ORDER_INSIGHTS", response?.data);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_REPORT_INSIGHT_LOADING", false);
        });
    });
  },
  getReportCanellationReasons(
    { commit, getters, state },
    { data, backgroundLoad, force }
  ) {
    if (!backgroundLoad || !state.reportOrders?.length)
      commit("SET_REPORT_CANCELLATION_REASONS_LOADING", true);
    const restaurantId = getters.getRestaurantId;
    return new Promise((resolve, reject) => {
      this.$axios
        .get(
          `/restaurants/${restaurantId}/audit_reports/cancellation_reasons`,
          {
            params: { ...getters.getDateRangePayload(data) }
          }
        )
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_REPORT_CANCELLATION_REASONS", response?.data);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_REPORT_CANCELLATION_REASONS_LOADING", false);
        });
    });
  },
  getBusinessGraphData(
    { commit, getters, state },
    { data, backgroundLoad, force }
  ) {
    if (!backgroundLoad || !state.businessGraphData?.length)
      commit("SET_BUSINESS_GRAPH_DATA_LOADING", true);
    let params = getters.getDateRangePayload(data);
    params.period = data.period;
    params.type = data.type;
    params.range = state.selectedTimeRange;
    return new Promise((resolve, reject) => {
      this.$axios
        .get(`/reports/business_graph`, {
          params: params,
          ...$nuxt.$configData?.apiV2Config
        })
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_BUSINESS_GRAPH_DATA", response?.data);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_BUSINESS_GRAPH_DATA_LOADING", false);
        });
    });
  },
  getPeakHours({ commit, getters, state }, { data, backgroundLoad }) {
    if (!backgroundLoad || !state.businessGraphData?.length)
      commit("SET_PEAK_HOURS_LOADING", true);
    let params = getters.getDateRangePayload(data);
    params.period = data.period;
    return new Promise((resolve, reject) => {
      this.$axios
        .get(`/reports/peak_hours`, {
          params: params,
          ...$nuxt.$configData?.apiV2Config
        })
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_PEAK_HOURS", response?.data);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_PEAK_HOURS_LOADING", false);
        });
    });
  },
  getOrderStages({ commit, getters, state }, { data, backgroundLoad }) {
    if (!backgroundLoad || !state.businessGraphData?.length)
      commit("SET_ORDER_STAGES_LOADING", true);
    let params = getters.getDateRangePayload(data);
    return new Promise((resolve, reject) => {
      this.$axios
        .get(`/reports/order_stages`, {
          params: params,
          ...$nuxt.$configData?.apiV2Config
        })
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_ORDER_STAGES", response?.data);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_ORDER_STAGES_LOADING", false);
        });
    });
  },
  getOrderDuration({ commit, getters, state }, { data, backgroundLoad }) {
    if (!backgroundLoad || !state.businessGraphData?.length)
      commit("SET_ORDER_DURATION_LOADING", true);
    let params = getters.getDateRangePayload(data);
    return new Promise((resolve, reject) => {
      this.$axios
        .get(`/reports/order_duration`, {
          params: params,
          ...$nuxt.$configData?.apiV2Config
        })
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_ORDER_DURATION", response?.data);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_ORDER_DURATION_LOADING", false);
        });
    });
  },
  getDeliveryLocations({ commit, getters, state }, { data, backgroundLoad }) {
    if (!backgroundLoad || !state.businessGraphData?.length)
      commit("SET_DELIVERED_LOCATIONS_LOADING", true);
    let params = getters.getDateRangePayload(data);
    return new Promise((resolve, reject) => {
      this.$axios
        .get(`/reports/delivery_locations`, {
          params: params,
          ...$nuxt.$configData?.apiV2Config
        })
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_DELIVERED_LOCATIONS", response?.data);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_DELIVERED_LOCATIONS_LOADING", false);
        });
    });
  },
  getStoreAvailability({ commit, getters, state }, { data, backgroundLoad }) {
    if (!backgroundLoad || !state.businessGraphData?.length)
      commit("SET_STORE_AVAILABILITY_LOADING", true);
    let params = getters.getDateRangePayload(data);
    return new Promise((resolve, reject) => {
      this.$axios
        .get(`/reports/store_availability`, {
          params: params,
          ...$nuxt.$configData?.apiV2Config
        })
        .then(function (response) {
          if (response?.status === 200) {
            commit("SET_STORE_AVAILABILITY", response?.data);
            resolve(response);
          } else {
            reject(response);
          }
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          commit("SET_STORE_AVAILABILITY_LOADING", false);
        });
    });
  }
};

export const getters = {
  getRestaurantId(state) {
    return (
      state.selectedRestaurants ||
      $nuxt.$store.state.restaurant.locationDetails?.restaurants[0]?.id
    );
  },
  getSelectedRestaurantsLabel(state) {
    if (
      $nuxt.$store.getters["restaurant/hasMultipleBrands"] &&
      $nuxt.$store.getters["restaurant/getAllBrands"]?.length ===
        state.selectedRestaurants?.length
    ) {
      return "All Brands";
    } else {
      return state.selectedRestaurants
        .map(
          (id) =>
            $nuxt.$store.getters["restaurant/getAllBrands"].find(
              (el) => el.id == id
            )?.name
        )
        ?.toString();
    }
  },
  getDateRangePayload(state) {
    return function (data) {
      let payload = {};
      payload.start_date = data?.start_date || state.selectedStartDate;
      payload.end_date = data?.end_date || state.selectedEndDate;
      payload.restaurant_ids =
        data?.selectedRestaurants || state.selectedRestaurants;
      return payload;
    };
  },
  getChannels(state) {
    return state.summary?.map((el) => el.channel) || [];
  },
  getTotalRevenue(state) {
    return state.summary?.reduce((tot, el) => tot + el.billable_amount, 0) || 0;
  },
  getTotalDiscountGiven(state) {
    return state.summary?.reduce((tot, el) => tot + el.discount, 0) || 0;
  },
  getSortedProducts(state) {
    if (state.products && state.products.length) {
      let products = JSON.parse(JSON.stringify(state.products));
      const channels = state.summary.map((el) => el.channel);
      products.sort(function (a, b) {
        // return a.zomato - b.zomato;
        return (
          channels.reduce((itemTot, channel) => itemTot + b[channel], 0) -
          channels.reduce((itemTot, channel) => itemTot + a[channel], 0)
        );
      });
      return products;
    }
  },
  getTotalLostRevenue(state) {
    return state.summary?.reduce((tot, el) => tot + el.lost_revenue, 0) || 0;
  },
  getOrdersListReport(state) {
    if (
      !state.selectedOrderFilter?.selectedChannels?.length &&
      !state.selectedOrderFilter?.selectedStages.length &&
      !state.selectedOrderFilter?.selectedPayments.length &&
      !state.ordersSearch
    ) {
      return state.reportOrders;
    } else {
      const search = state.ordersSearch?.toLowerCase() || "";
      let orders = JSON.parse(JSON.stringify(state.reportOrders));
      orders = orders?.filter((order) => {
        const channelFilterMatch =
          !state.selectedOrderFilter.selectedChannels.length ||
          state.selectedOrderFilter.selectedChannels.includes(order.source);

        const paymentFilterMatch =
          !state.selectedOrderFilter.selectedPayments.length ||
          state.selectedOrderFilter.selectedPayments.includes(order.mode);

        const stageFilterMatch =
          !state.selectedOrderFilter.selectedStages.length ||
          (state.selectedOrderFilter.selectedStages.includes("Completed")
            ? order.status == $nuxt.$constants.OrderStatuses.COMPLETED.id
            : false) ||
          (state.selectedOrderFilter.selectedStages.includes("Cancelled")
            ? order.status == $nuxt.$constants.OrderStatuses.CANCELLED.id
            : false) ||
          (state.selectedOrderFilter.selectedStages.includes("Active")
            ? order.status != $nuxt.$constants.OrderStatuses.COMPLETED.id &&
              order.status != $nuxt.$constants.OrderStatuses.CANCELLED.id
            : false);

        return channelFilterMatch && paymentFilterMatch && stageFilterMatch;
      });
      if (search) {
        orders = orders?.filter(
          ((order) => {
            return (
              String(order.invoice_id).indexOf(search) >= 0 ||
              String(order.source).toLowerCase().indexOf(search) >= 0 ||
              String(order.merchant_ref_id).indexOf(search) >= 0 ||
              String(order.partner_order_id).indexOf(search) >= 0 ||
              String(order.payable_amount).indexOf(search) >= 0 ||
              String(
                $nuxt.$constants.OrderStatuses.STATUSES.find(
                  (status) => status.id == order.status
                )?.label?.toLowerCase()
              ).indexOf(search) >= 0
            );
          }).bind(this)
        );
      }
      return orders;
    }
  },
  getTotalObjForSummaryReport(state) {
    let totalObj = {
      label: "Total",
      count: 0,
      billable_amount: 0,
      total_taxes: 0,
      discount: 0,
      total_external_discount: 0,
      merchant_receivable_amount: 0
    };
    state.summary?.forEach((channel) => {
      totalObj.count += channel.count;
      totalObj.billable_amount += channel.billable_amount;
      totalObj.total_taxes += channel.total_taxes;
      totalObj.discount += channel.discount;
      totalObj.total_external_discount += channel.total_external_discount;
      totalObj.merchant_receivable_amount += channel.merchant_receivable_amount;
    });
    return totalObj;
  }
};
